import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type PropsWithChildren, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentContext } from '../../context/useOpenXAgentContext';
import { OpenXAgentFaq } from './components/OpenXAgentFaq';
import { OpenXAgentLegalNote } from './components/OpenXAgentLegalNote';
import { OpenXAgentMenuContext } from './context/OpenXAgentMenuContext';
import { FOOTER_STYLES, getHeaderStyles, getPanelStyles } from './OpenXAgentMenu.styles';

type SubScreen = {
  title: string;
  content: ReactNode;
};

type OpenXAgentMenuProps = PropsWithChildren<{
  isDragging?: boolean;
  onDragStart?: (event: MouseEvent<HTMLDivElement>) => void;
}>;

export const OpenXAgentMenu = ({ children, isDragging = false, onDragStart }: OpenXAgentMenuProps) => {
  const { t } = useTranslation();
  const { isMenuOpen, setIsMenuOpen } = useOpenXAgentContext();
  const [subScreen, setSubScreen] = useState<SubScreen | null>(null);

  useEffect(() => {
    if (!isMenuOpen) setSubScreen(null);
  }, [isMenuOpen]);

  const handleClose = useCallback(() => {
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  const navigateBack = useCallback(() => setSubScreen(null), []);

  const navigateTo = useCallback((content: ReactNode, title: string) => {
    setSubScreen({ content, title });
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      setSubScreen(null);
    }
  }, [isMenuOpen]);

  return (
    <OpenXAgentMenuContext.Provider value={{ navigateTo, navigateBack }}>
      <Box role="dialog" aria-label={t('OpenX Agent Menu')} aria-hidden={!isMenuOpen} sx={getPanelStyles(isMenuOpen)}>
        <Box onMouseDown={onDragStart} sx={getHeaderStyles(isDragging)}>
          <IconButton
            aria-label={subScreen ? t('back') : t('close menu')}
            onClick={subScreen ? navigateBack : handleClose}
            size="small"
            sx={{ color: 'text.secondary', p: 0.5, '&:hover': { backgroundColor: 'action.hover' } }}
          >
            {subScreen ? <ArrowBack fontSize="small" /> : <Close fontSize="small" />}
          </IconButton>

          <Typography variant="h2" sx={{ fontWeight: 'fontWeightBold', color: 'text.primary', flex: 1 }}>
            {subScreen ? subScreen.title : t('OpenX Agent Menu')}
          </Typography>

          {!!subScreen && (
            <IconButton
              aria-label={t('close menu')}
              onClick={handleClose}
              size="small"
              sx={{ color: 'text.secondary', p: 0.5, '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>{subScreen ? subScreen.content : children}</Box>

        {!subScreen && (
          <Box sx={FOOTER_STYLES}>
            <Link
              component="button"
              underline="hover"
              variant="body2"
              onClick={() => navigateTo(<OpenXAgentLegalNote />, t('Legal Note'))}
            >
              {t('Legal Note')}
            </Link>

            <Divider orientation="vertical" sx={{ height: '14px', alignSelf: 'center' }} />

            <Link
              component="button"
              underline="hover"
              variant="body2"
              onClick={() => navigateTo(<OpenXAgentFaq />, t('FAQ'))}
            >
              {t('FAQ')}
            </Link>
          </Box>
        )}
      </Box>
    </OpenXAgentMenuContext.Provider>
  );
};
